// Which surveys a save reaches (template-page spec §3): the newest 20 using
// this template, through survey search's typed `template` filter, since the
// survey list is paged and can't be filtered in the browser.

import { Badge, Spinner } from "@pej/kore/components";
import { Link } from "@tanstack/react-router";
import { useSurveysSearch } from "@/api/surveys";
import type { Template } from "@/api/types";
import { PageError, useRetry } from "@/components/page-states";
import { SectionCard } from "@/components/section-page/section-card";
import { TEMPLATE_SECTION } from "./template-sections";

export const USED_BY_PAGE_SIZE = 20;

export function TemplateUsedBy({ template }: { template: Template }) {
    const search = useSurveysSearch(template.workspaceId, {
        filters: { template: { type: "Eq", values: [template.id] } },
        pageSize: USED_BY_PAGE_SIZE,
        orderBy: { field: "CreatedAt", order: "Desc" },
    });
    const items = search.data?.items ?? [];
    const more = (search.data?.totalCount ?? 0) - items.length;
    const searchRetry = useRetry(search);

    return (
        <SectionCard id={TEMPLATE_SECTION.usedBy} title="Used by">
            {searchRetry.failed ? (
                <PageError level={3} compact {...searchRetry.props} />
            ) : search.isLoading ? (
                <Spinner />
            ) : items.length === 0 ? (
                <p className="text-sm text-kore-text-muted">No survey uses this template yet.</p>
            ) : (
                <>
                    <ul className="flex flex-col divide-y divide-kore-border">
                        {items.map((survey) => (
                            <li key={survey.id} className="flex items-center justify-between gap-3 py-2">
                                <Link
                                    to="/w/$workspaceId/surveys/$surveyId"
                                    params={{ workspaceId: template.workspaceId, surveyId: survey.id }}
                                    className="min-w-0 truncate text-sm font-medium hover:underline"
                                >
                                    {survey.name}
                                </Link>
                                <Badge tone={survey.active ? "success" : "neutral"}>
                                    {survey.active ? "Active" : "Inactive"}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                    {more > 0 && <p className="mt-2 text-sm text-kore-text-muted">and {more} more</p>}
                </>
            )}
        </SectionCard>
    );
}
